import { WordInfo, KanjiInfo } from './types';
import { wordInfoCache } from './cache';
import { debug, info } from './logger';
import { analyzeWordType } from './tokenizer';
import { getRomaji, hasKanji, isPunctuation, katakanaToHiragana } from './converter';
import { withErrorHandling } from './error';

/**
 * 获取单词详细信息
 * 1. 检查缓存
 * 2. 如果是平假名，直接返回
 * 3. 调用API获取详细信息
 * 4. 处理错误情况和各种边缘情况
 */
export const getWordInfo = withErrorHandling(
  async (word: string): Promise<WordInfo> => {
    if (!word || word.trim() === '') {
      throw new Error('单词不能为空');
    }

    // 检查缓存
    if (wordInfoCache.has(word)) {
      const cached = wordInfoCache.get(word);
      debug(`命中缓存: ${word}`);
      return cached as WordInfo;
    }

    // 如果是标点符号，直接返回
    if (isPunctuation(word)) {
      const info: WordInfo = {
        text: word,
        isKanjiWord: false,
        kanjis: [{
          kanji: word,
          reading: word
        }],
        romaji: word,
        pos_detail_1: '',
        pos_detail_2: '',
        pos_detail_3: '',
        basic_form: word,
        conjugated_type: '',
        conjugated_form: ''
      };
      wordInfoCache.set(word, info);
      return info;
    }

    // 获取词性信息
    const wordType = await analyzeWordType(word);

    // 确保读音是平假名（kuromoji返回的是片假名）
    const reading = wordType?.reading ? katakanaToHiragana(wordType.reading) : word;

    // 构建汉字信息数组
    const kanjis: KanjiInfo[] = [];

    if (hasKanji(word)) {
      // 如果有汉字，需要分别处理每个字符
      const chars = word.split('');
      const readingChars = reading.split('');

      // 如果读音长度与原词不同，可能是整体读音，此时为每个汉字分配相同的读音
      if (chars.length !== readingChars.length) {
        for (let i = 0; i < chars.length; i++) {
          const char = chars[i];
          if (hasKanji(char)) {
            kanjis.push({
              kanji: char,
              reading: reading // 使用整体读音
            });
          } else {
            kanjis.push({
              kanji: char,
              reading: char
            });
          }
        }
      } else {
        // 如果读音长度与原词相同，可以一一对应
        for (let i = 0; i < chars.length; i++) {
          const char = chars[i];
          const readingChar = readingChars[i];
          kanjis.push({
            kanji: char,
            reading: hasKanji(char) ? readingChar : char
          });
        }
      }
    } else {
      // 如果没有汉字，直接使用原词
      kanjis.push({
        kanji: word,
        reading: reading
      });
    }

    // 构建基本信息
    const info: WordInfo = {
      text: word,
      isKanjiWord: hasKanji(word),
      kanjis: kanjis,
      romaji: await getRomaji(reading),
      pos: wordType?.pos,
      pos_detail_1: wordType?.pos_detail_1,
      pos_detail_2: wordType?.pos_detail_2,
      pos_detail_3: wordType?.pos_detail_3,
      basic_form: wordType?.basic_form,
      conjugated_type: wordType?.conjugated_type,
      conjugated_form: wordType?.conjugated_form
    };

    // 缓存并返回
    wordInfoCache.set(word, info);
    return info;
  },
  // 默认值
  {
    text: '',
    isKanjiWord: false,
    kanjis: [],
    romaji: ''
  },
  '获取单词信息'
);